import { FC, PropsWithChildren, ReactNode, useEffect } from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { IconName } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button, ButtonProps, Heading, HeadingProps, Text, TextProps, Truncated } from '@frontend/design-system';
import { useChartContext } from '../chart.provider';
import { PDFDetails, useExportChartToPDF } from '../hooks';

interface HeaderProps {
  title?: ReactNode;
  subTitle?: ReactNode;
  action?: ReactNode;
  containerStyles?: SerializedStyles;
}

export interface HeaderV2Namespace extends FC<HeaderProps> {
  Title: FC<PropsWithChildren<TitleProps>>;
  Action: typeof Action;
  PdfExportAction: typeof PdfExportAction;
  FilterText: typeof FilterText;
}

export const HeaderV2: HeaderV2Namespace = ({ title, subTitle, action, containerStyles }) => {
  return (
    <div css={[styles.headerContainer, containerStyles]}>
      <div css={styles.titleRow}>{title}</div>
      <div css={styles.actionBar}>{action}</div>
      <div css={styles.subtitle}>{subTitle}</div>
    </div>
  );
};

HeaderV2.displayName = 'HeaderV2';

interface TitleProps {
  leftElement?: ReactNode;
  rightElement?: ReactNode;
  infoTip?: ReactNode;
  headerLevel?: HeadingProps['level'];
}
const Title: FC<PropsWithChildren<TitleProps>> = ({
  leftElement,
  rightElement,
  infoTip,
  children,
  headerLevel = 3,
}) => {
  return (
    <div css={styles.titleContainer}>
      {leftElement}
      <Heading level={headerLevel} css={styles.title}>
        <Truncated as='span'>{children}</Truncated>
        {infoTip}
      </Heading>
      {rightElement}
    </div>
  );
};

export type ChartHeaderAction = ButtonProps & {
  label: string;
  omit?: boolean;
} & (
    | {
        hoverText?: never;
        iconName?: IconName;
        showLabel?: boolean;
      }
    | {
        hoverText?: string;
        iconName?: never;
        showLabel?: never;
      }
  );

const Action: FC<PropsWithChildren<ChartHeaderAction>> = ({
  omit,
  hoverText,
  iconName,
  showLabel,
  variant = 'tertiary',
  ...rest
}) => {
  const { isSmallMode } = useChartContext();

  if (omit) {
    return null;
  }
  return (
    <Button {...rest} className='action-button' iconName={iconName} key={rest.label} size='small' variant={variant}>
      {!isSmallMode && showLabel && rest.label}
    </Button>
  );
};

// TODO: Move this to a separate file
export const PdfExportAction: FC<{ pdfDetails?: PDFDetails; pdfTitle: string }> = ({ pdfDetails = [], pdfTitle }) => {
  const { chartContainer, setShowLoader } = useChartContext();
  const { exportToPDF, isExportingPDF } = useExportChartToPDF(pdfDetails);
  const { t } = useTranslation('analytics');

  useEffect(() => {
    setShowLoader(isExportingPDF);
  }, [isExportingPDF, setShowLoader]);

  return (
    <Action iconName='download' label={t('Export to PDF')} onClick={() => exportToPDF(pdfTitle, chartContainer)} />
  );
};

export const FilterText: FC<{ filterHintText?: string } & TextProps> = ({ filterHintText, ...rest }) => {
  return (
    <Text weight='bold' color='light' size='medium' {...rest}>
      {filterHintText}
    </Text>
  );
};

HeaderV2.Title = Title;
HeaderV2.Action = Action;
HeaderV2.PdfExportAction = PdfExportAction;
HeaderV2.FilterText = FilterText;

const styles = {
  headerContainer: css`
    display: grid;
    row-gap: ${theme.spacing(0.5)};
    column-gap: ${theme.spacing(2)};
    grid-template-columns: max(100px, 1fr) auto;
    grid-template-rows: auto;
    grid-template-areas: 'title action' 'subtitle subtitle';
    align-items: center;
    @container (max-width: ${breakpoints.xsmall.max}px) {
      grid-template-areas: 'title action' 'subtitle action';
      align-items: start;
    }
  `,
  titleRow: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    min-width: 100px;
    overflow: hidden;
    grid-area: title;
  `,
  actionBar: css`
    width: fit-content;
    flex-shrink: 0;
    grid-area: action;
    justify-self: end;
    @container (max-width: ${breakpoints.xsmall.max}px) {
      grid-row: 1 / span 2;
      align-self: start;
    }
  `,
  subtitle: css`
    grid-area: subtitle;
  `,
  titleContainer: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    overflow: hidden;
  `,
  title: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    overflow: hidden;
    min-width: 0;
    @container (max-width: ${breakpoints.xsmall.max}px) {
      font-size: ${theme.font.size.large};
    }
  `,
};
